import { Heading } from '@ksyos/design-system';
import { useLocation, useParams } from '@ksyos/router';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import type { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import type { IServiceGroup } from '../../../../common/interfaces/serviceGroup';
import type { IServiceGroupMembership } from '../../../../common/interfaces/serviceGroupMembership';
import type {
    IServiceGroupWithServices,
    IServicesByServiceGroupForEpisodeCategory,
} from '../../../../common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { IServiceOrganizationSettings } from '../../interfaces/serviceOrganizationSettings';
import type { GetInactiveServiceOrganizationSettingsResultItem } from './OrganizationInactiveServiceOrganizationSettings';
import { compareByNestedServiceTypeSortOrder } from '../../../../common/utils/serviceSortUtils';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import { AddButtonHeader } from '../../elements/AddButtonHeader';
import { ButtonHeader } from '../../elements/ButtonHeader';
import { ServiceGroupButtonRow } from '../../elements/ServiceGroupButtonRow';
import { OrganizationInactiveServiceOrganizationSettings } from './OrganizationInactiveServiceOrganizationSettings';
import { ServiceGroupOrganizationSettings } from './serviceSettings/ServiceGroupOrganizationSettings';
import { ServiceOrganizationSettingStopModal } from './serviceSettings/ServiceOrganizationSettingStopModal';

interface IServiceOrganizationSettingsGroupedByServiceGroup {
    [key: string]: IServiceOrganizationSettings[];
}

export function OrganizationCarePath() {
    const { organizationId, episodeCategory } = useParams() as {
        episodeCategory: EpisodeCategory;
        organizationId: string;
    };

    assertHasAuthorization(ManagerAuthorization.organizationRead);
    const canEdit = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
    const abortableFetch = useAbortableFetch();
    const { pathname } = useLocation();
    const [serviceOrganizationSettingsGroupedByServiceGroup, setServiceOrganizationSettingsGroupedByServiceGroup] =
        useState<IServiceOrganizationSettingsGroupedByServiceGroup>({});
    const [selectedServiceOrganizationSettings, setSelectedServiceOrganizationSettings] = useState<
        IServiceOrganizationSettings[]
    >([]);
    const [inactiveServiceOrganizationSettings, setInactiveServiceOrganizationSettings] = useState<
        GetInactiveServiceOrganizationSettingsResultItem[]
    >([]);
    const [activeServiceOrganizationSettings, setActiveServiceOrganizationSettings] = useState<
        IServiceOrganizationSettings[]
    >([]);
    const [serviceGroups, setServiceGroups] = useState<IServiceGroup[]>([]);
    const [tabNames, setTabNames] = useState<string[]>([]);
    const [selectedTabName, setSelectedTabName] = useState<undefined | string>();
    const [addableServiceGroups, setAddableServiceGroups] = useState<IServiceGroupWithServices[]>([]);
    const [showStopAllServiceOrganizationSettingsModal, setShowStopAllServiceOrganizationSettingModal] =
        useState(false);

    const inactiveSettingsName = translateKey('episodeCategory.inactiveSettings');

    useEffect(() => {
        loadActiveServiceOrganizationSettings();
        loadInactiveServiceOrganizationSettings();
        loadAddableServiceGroups();
        loadServiceGroups();
    }, [organizationId, episodeCategory]);

    useEffect(() => {
        const serviceGroupNames = Object.keys(serviceOrganizationSettingsGroupedByServiceGroup);

        if (serviceGroups.length === 0 || serviceGroupNames.length === 0) {
            return;
        }

        const tabNamesToSet: string[] = [];
        // If episode category only contains one service group, show tabs if there are inactive settings.
        if (serviceGroups.length === 1) {
            if (inactiveServiceOrganizationSettings.length > 0) {
                const activeSettingsName = translateKey('episodeCategory.activeSettings');
                tabNamesToSet.push(activeSettingsName);
            }
        } else {
            tabNamesToSet.push(...serviceGroupNames);
        }

        if (inactiveServiceOrganizationSettings.length > 0) {
            tabNamesToSet.push(inactiveSettingsName);
        }
        setTabNames(tabNamesToSet);
    }, [serviceGroups, serviceOrganizationSettingsGroupedByServiceGroup]);

    useEffect(() => {
        setSelectedTabName(tabNames.find((tab) => tab !== inactiveSettingsName));
    }, [tabNames]);

    if (serviceGroups.length === 0) {
        return null;
    }

    return (
        <>
            {!canEdit && (
                <Heading
                    mb={6}
                    size="xlarge"
                >
                    {translateKey(`episodeCategoryEnum.${episodeCategory}`)}
                </Heading>
            )}
            {!addableServiceGroups.length && canEdit && (
                <ButtonHeader
                    dropdownMenuItems={getDropdownMenuItems()}
                    style={{ marginTop: 0 }}
                    title={<Heading size="xlarge">{translateKey(`episodeCategoryEnum.${episodeCategory}`)}</Heading>}
                />
            )}
            {addableServiceGroups.length > 0 && canEdit && (
                <AddButtonHeader
                    buttonText={translateKey('service.addServiceButton')}
                    dropdownMenuItems={getDropdownMenuItems()}
                    link={{
                        pathname: `/organizations/${organizationId}/view/addCarePath/${episodeCategory}`,
                        state: {
                            previousLocation: `${pathname}/carePaths/${episodeCategory}`,
                        },
                    }}
                    style={{ marginTop: 0 }}
                    title={<Heading size="xlarge">{translateKey(`episodeCategoryEnum.${episodeCategory}`)}</Heading>}
                />
            )}

            {showStopAllServiceOrganizationSettingsModal && activeServiceOrganizationSettings.length && (
                <ServiceOrganizationSettingStopModal
                    onClose={() => setShowStopAllServiceOrganizationSettingModal(false)}
                    organizationName={activeServiceOrganizationSettings[0].organization.name}
                    reloadServiceOrganizationSetting={reloadActiveServiceOrganizationSettings}
                    serviceOrganizationSettingIds={activeServiceOrganizationSettings.map(
                        (serviceOrganizationSetting) => serviceOrganizationSetting.id
                    )}
                    serviceOrServiceGroupOrCarePathName={translateKey(`episodeCategoryEnum.${episodeCategory}`)}
                    startDate={orderBy(activeServiceOrganizationSettings, 'startDate', 'desc')[0].startDate}
                    stopCarePath
                />
            )}

            {selectedTabName && tabNames.length > 0 && (
                <ServiceGroupButtonRow
                    onServiceGroupClick={serviceGroupClickHandler}
                    selectedServiceGroupName={selectedTabName}
                    serviceGroupNames={tabNames}
                />
            )}
            {selectedServiceOrganizationSettings && selectedTabName !== inactiveSettingsName && (
                <ServiceGroupOrganizationSettings
                    episodeCategory={episodeCategory}
                    organizationId={organizationId}
                    reloadServiceOrganizationSettings={reloadActiveServiceOrganizationSettings}
                    serviceOrganizationSettings={selectedServiceOrganizationSettings}
                />
            )}
            {selectedTabName === inactiveSettingsName && (
                <OrganizationInactiveServiceOrganizationSettings
                    inactiveServiceOrganizationSettings={inactiveServiceOrganizationSettings}
                />
            )}
        </>
    );

    async function loadActiveServiceOrganizationSettings() {
        if (!organizationId || !episodeCategory) {
            return null;
        }

        const serviceOrganizationSettingsByServiceGroup = await getServiceOrganizationSettingsGroupedByServiceGroup();
        setServiceOrganizationSettingsGroupedByServiceGroup(serviceOrganizationSettingsByServiceGroup);
        const serviceGroupNames = Object.keys(serviceOrganizationSettingsByServiceGroup);

        if (serviceGroupNames.length > 0) {
            setSelectedServiceOrganizationSettings(serviceOrganizationSettingsByServiceGroup[serviceGroupNames[0]]);
        } else {
            setSelectedServiceOrganizationSettings([]);
        }
    }

    async function getServiceOrganizationSettingsGroupedByServiceGroup() {
        const serviceOrganizationSettings = await fetchServiceOrganizationSettings(organizationId, episodeCategory);
        serviceOrganizationSettings.sort(compareByNestedServiceTypeSortOrder);

        setActiveServiceOrganizationSettings(serviceOrganizationSettings);

        return serviceOrganizationSettings.reduce(
            (
                serviceOrganizationSettingByGroup: IServiceOrganizationSettingsGroupedByServiceGroup,
                serviceOrganizationSetting
            ) => {
                if (serviceOrganizationSettingByGroup[serviceOrganizationSetting.serviceGroupName]) {
                    serviceOrganizationSettingByGroup[serviceOrganizationSetting.serviceGroupName].push(
                        serviceOrganizationSetting
                    );
                } else {
                    serviceOrganizationSettingByGroup[serviceOrganizationSetting.serviceGroupName] = [
                        serviceOrganizationSetting,
                    ];
                }

                return serviceOrganizationSettingByGroup;
            },
            {}
        );
    }

    async function reloadActiveServiceOrganizationSettings(): Promise<void> {
        if (!organizationId || !episodeCategory) {
            return;
        }

        const serviceOrganizationSettingsByServiceGroup = await getServiceOrganizationSettingsGroupedByServiceGroup();
        setServiceOrganizationSettingsGroupedByServiceGroup(serviceOrganizationSettingsByServiceGroup);

        const selectedServiceGroupName = selectedServiceOrganizationSettings[0].serviceGroupName;
        setSelectedServiceOrganizationSettings(serviceOrganizationSettingsByServiceGroup[selectedServiceGroupName]);
    }

    async function fetchServiceOrganizationSettings(organizationId: string, episodeCategory: EpisodeCategory) {
        const serviceOrganizationSettings = (
            await abortableFetch.fetchJson(
                `/api/serviceOrganizationSettings?organizationId=${organizationId}&isActive=true&episodeCategory=${episodeCategory}&excludeServicesWithoutServiceGroup=true&includeDeletedGroupHistory=true`,
                'GET'
            )
        ).json.items as IServiceOrganizationSettings[];
        const serviceGroupMemberships = (
            await abortableFetch.fetchJson(
                `/api/serviceGroupMemberships?organizationId=${organizationId}&isActive=true`,
                'GET'
            )
        ).json.items as IServiceGroupMembership[];

        const serviceOrganizationSettingsWithServiceGroupName = serviceOrganizationSettings.map(
            (serviceOrganizationSetting) => {
                const serviceGroupMembership = serviceGroupMemberships.find(
                    (item) => item.service.id === serviceOrganizationSetting.service.id
                );

                return {
                    ...serviceOrganizationSetting,
                    serviceGroupName: serviceGroupMembership!.serviceGroup.name,
                    serviceGroupId: serviceGroupMembership!.serviceGroup.id,
                };
            }
        );

        return serviceOrganizationSettingsWithServiceGroupName;
    }

    function serviceGroupClickHandler(serviceGroup: { id: string }) {
        if (
            serviceOrganizationSettingsGroupedByServiceGroup &&
            serviceOrganizationSettingsGroupedByServiceGroup[serviceGroup.id]
        ) {
            setSelectedServiceOrganizationSettings(serviceOrganizationSettingsGroupedByServiceGroup[serviceGroup.id]);
        }

        setSelectedTabName(serviceGroup.id);
    }

    async function loadInactiveServiceOrganizationSettings() {
        const result = await abortableFetch.fetchJson(
            `/api/organizations/${organizationId}/episodeCategories/${episodeCategory}/inactiveServiceOrganizationSettings`,
            'GET'
        );

        setInactiveServiceOrganizationSettings(result.json.items);
    }

    async function loadAddableServiceGroups() {
        const result = (
            await abortableFetch.fetchJson(
                `/api/organizations/${organizationId}/addableServiceGroups?episodeCategory=${episodeCategory}`,
                'GET'
            )
        ).json.items[0] as IServicesByServiceGroupForEpisodeCategory;
        setAddableServiceGroups(result?.serviceGroups ?? []);
    }

    async function loadServiceGroups() {
        const result = await abortableFetch.fetchJson(`/api/episodeCategories/${episodeCategory}/serviceGroups`, 'GET');

        setServiceGroups(result.json.items);
    }

    function getDropdownMenuItems() {
        if (
            activeServiceOrganizationSettings.every((serviceOrganizationSetting) => serviceOrganizationSetting.endDate)
        ) {
            return;
        }

        return [
            {
                text: translateKey(
                    'stopServiceOrganizationSettings.stopAll',
                    translateKey(`episodeCategoryEnum.${episodeCategory}`)
                ),
                onClick: () => setShowStopAllServiceOrganizationSettingModal(true),
            },
        ];
    }
}
